import { Avatar, EmptyState, LabelCaps, SkeletonRows } from '@pm/ui';
import { UsersRound } from 'lucide-react';
import { UserPicker } from '@/components/pickers';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { useContextFunctions, useFunctions, useInvalidating, wfKeys } from '@/lib/queries';
import { useToast } from '@/lib/toast';

/** RULE-059 — quem exerce cada função neste cliente/contexto (usado na geração por workflow). */
export function ContextTeamSection({ contextId }: { contextId: string }) {
  const toast = useToast();
  const { can } = useAuth();
  const { data: functions = [], isLoading } = useFunctions();
  const { data: assignments = [] } = useContextFunctions(contextId);
  const set = useInvalidating(
    ({ functionId, userId }: { functionId: string; userId: string | null }) =>
      api.put(`/contexts/${contextId}/functions`, { function_id: functionId, user_id: userId }),
    [wfKeys.contextFunctions(contextId)],
  );
  const edit = can('clients.edit');

  return (
    <div className="rounded-md border border-border bg-surface-1 p-4">
      <LabelCaps>Equipe por função</LabelCaps>
      <p className="mt-1 text-sm text-text-2">
        Workflows atribuem tarefas por função. Aqui você define quem responde por cada função neste
        cliente; sem definição, o sistema usa a única pessoa da organização com a função, ou deixa
        não atribuída.
      </p>
      {isLoading ? (
        <SkeletonRows rows={2} />
      ) : functions.length === 0 ? (
        <EmptyState
          icon={UsersRound}
          title="Nenhuma função cadastrada"
          description="Cadastre funções em Times."
          className="py-6"
        />
      ) : (
        <ul className="mt-3 grid gap-2 sm:grid-cols-2">
          {functions.map((f) => {
            const current = assignments.find((a) => a.function.id === f.id);
            return (
              <li
                key={f.id}
                className="flex items-center gap-3 rounded border border-border px-3 py-2"
              >
                <span className="min-w-[120px] text-sm font-medium">{f.name}</span>
                {edit ? (
                  <div className="flex-1">
                    <UserPicker
                      value={current?.user.id ?? null}
                      onChange={(uid) =>
                        set.mutate(
                          { functionId: f.id, userId: uid },
                          { onError: (e) => toast.error(e) },
                        )
                      }
                      placeholder="Não definido"
                      size="sm"
                    />
                  </div>
                ) : current ? (
                  <span className="inline-flex items-center gap-2 text-sm">
                    <Avatar name={current.user.name} size="xs" /> {current.user.name}
                  </span>
                ) : (
                  <span className="text-sm text-text-3">Não definido</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
